/**
 * Lift2Fit — admin: System (`#/admin/system`).
 *
 * A brief, honest health panel: database size, row counts for the main tables,
 * the migrations applied and when, API version, server uptime and the counts
 * that reveal trouble (accounts with no activity, sessions left active,
 * abandoned sessions, invitations pending). READ-ONLY — there is no destructive
 * control on this screen and no endpoint behind it that writes anything.
 */
import { api } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { AlertIcon, MonitorIcon, ShieldIcon } from '@/lib/icons';
import {
  AppLinkButton,
  EmptyState,
  LoadingPanel,
  Panel,
  ScreenHeader,
  StatRow,
  StatusChip,
} from '../components';
import { useAsync } from '../hooks';
import { formatCompactCount, formatCount } from '@/lib/format';
import { ADMIN_EYEBROW, AdminStat } from './shared';

function formatBytes(value: number | null): string {
  if (value === null) return 'unknown';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function AdminSystemScreen() {
  const state = useAsync(() => api.adminSystem(), []);
  const system = state.data;

  if (state.loading && !system) {
    return <LoadingPanel label="Reading the system health" rows={4} />;
  }

  if (!system) {
    return (
      <EmptyState
        title="The health panel did not load"
        body={state.error ?? 'The admin API did not answer. Sign in as an administrator and try again.'}
        icon={<AlertIcon className="h-8 w-8 text-accent" />}
        action={
          <AppLinkButton to="/admin" variant="outline">
            Back to the overview
          </AppLinkButton>
        }
      />
    );
  }

  const { api: apiInfo, database, tables, migrations, health } = system;
  const trouble =
    health.sessionsActive +
    health.sessionsAbandoned +
    health.invitationsExpired +
    health.accountsNoActivity;

  return (
    <div className="flex flex-col gap-6" data-testid="admin-system">
      <ScreenHeader
        eyebrow={ADMIN_EYEBROW}
        title="System"
        badge={<StatusChip tone="accent">Read-only</StatusChip>}
        lead="A brief health panel: the database, the migrations, the server and the counts that reveal trouble. Nothing on this screen writes anything."
        actions={
          <AppLinkButton to="/admin/audit" variant="outline">
            <ShieldIcon className="text-[14px]" />
            Audit trail
          </AppLinkButton>
        }
      />

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="admin-system-health">
        <AdminStat
          label="API version"
          value={apiInfo.version || '—'}
          hint={`${apiInfo.nodeEnv} · up ${formatUptime(apiInfo.uptimeSeconds)}`}
        />
        <AdminStat label="Database size" value={formatBytes(database.sizeBytes)} hint={database.engine} />
        <AdminStat label="Migrations applied" value={formatCompactCount(migrations.length)} valueTitle={formatCount(migrations.length)} hint="newest last" />
        <AdminStat
          label="Signals to watch"
          value={formatCompactCount(trouble)}
          valueTitle={formatCount(trouble)}
          hint="abandoned or active sessions, stale invites, idle accounts"
          tone={trouble > 0 ? 'text-warning' : 'text-success'}
        />
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="Health counts" subtitle="Counts that reveal trouble — never a destructive control." testId="admin-system-signals">
          <StatRow label="Accounts with no activity" value={formatCompactCount(health.accountsNoActivity)} valueTitle={formatCount(health.accountsNoActivity)} tone={health.accountsNoActivity > 0 ? 'warning' : 'success'} />
          <StatRow label="Sessions left active" value={formatCompactCount(health.sessionsActive)} valueTitle={formatCount(health.sessionsActive)} tone={health.sessionsActive > 0 ? 'warning' : 'success'} />
          <StatRow label="Sessions abandoned" value={formatCompactCount(health.sessionsAbandoned)} valueTitle={formatCount(health.sessionsAbandoned)} tone={health.sessionsAbandoned > 0 ? 'warning' : 'success'} />
          <StatRow label="Invitations pending" value={formatCompactCount(health.invitationsPending)} valueTitle={formatCount(health.invitationsPending)} />
          <StatRow label="Invitations expired" value={formatCompactCount(health.invitationsExpired)} valueTitle={formatCount(health.invitationsExpired)} tone={health.invitationsExpired > 0 ? 'warning' : 'default'} />
          <StatRow label="Offers active" value={formatCompactCount(health.offersActive)} valueTitle={formatCount(health.offersActive)} />
          <StatRow label="Payments due" value={formatCompactCount(health.paymentsDue)} valueTitle={formatCount(health.paymentsDue)} tone={health.paymentsDue > 0 ? 'warning' : 'default'} />
        </Panel>

        <Panel title="Server" subtitle="The process serving both interfaces." testId="admin-system-server">
          <StatRow label="API" value={apiInfo.name} />
          <StatRow label="Version" value={apiInfo.version || '—'} />
          <StatRow label="Environment" value={apiInfo.nodeEnv || '—'} />
          <StatRow label="Uptime" value={formatUptime(apiInfo.uptimeSeconds)} />
          <StatRow label="Engine" value={database.engine} />
          <StatRow label="Database size" value={formatBytes(database.sizeBytes)} />
          <StatRow label="Server time" value={formatDate(apiInfo.serverTime)} />
          <p className="text-[11px] text-ink-muted">
            Snapshot generated {formatDate(system.generatedAt)}. Row counts are live at read time.
          </p>
        </Panel>
      </div>

      <Panel title="Main tables" subtitle="Live row counts for the tables both products write." testId="admin-system-tables">
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 md:grid-cols-3">
          {tables.map((table) => (
            <StatRow key={table.name} label={table.name} value={formatCount(table.rows)} />
          ))}
        </div>
      </Panel>

      <Panel
        title="Migrations applied"
        subtitle="Every schema file the database has run, in order."
        testId="admin-system-migrations"
        actions={<MonitorIcon className="text-[14px] text-ink-muted" />}
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] border-collapse text-left">
            <thead>
              <tr className="border-b border-hairline text-[10px] uppercase tracking-[0.1em] text-ink-muted">
                <th className="py-2 pr-3 font-bold">File</th>
                <th className="py-2 font-bold">Applied</th>
              </tr>
            </thead>
            <tbody>
              {migrations.map((migration) => (
                <tr key={migration.filename} className="border-b border-hairline last:border-b-0">
                  <td className="py-2 pr-3 font-mono text-[12px] text-ink">{migration.filename}</td>
                  <td className="py-2 text-[12px] text-ink-muted">{formatDate(migration.appliedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
